import { useCallback, useEffect, useMemo, useRef } from "react";
import { DataTable } from "@/components/fw/data-table/DataTable";
import { useDataTable } from "@/components/fw/data-table/useDataTable";
import type { FwQueryState } from "@/components/fw/data-table/useFwQuery";
import type { StorefrontOrderDto } from "@/lib/storefront-api";
import { useCheckoutFormState } from "../../hooks/useCheckoutFormState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { orderColumns } from "./columns";
import { orderFilters } from "./filters";
import { orderQuickFilters } from "./quick-filters";

type OrderRow = StorefrontOrderDto;

export interface OrderTableProps {
	query: FwQueryState<OrderRow>;
}

export function OrderTable({ query }: OrderTableProps) {
	const translate = useTranslate();
	const checkoutFormState = useCheckoutFormState();
	const storefrontSettingsState = useStorefrontSettingsState();
	const isUniversity =
		storefrontSettingsState.get().settings?.IsUniversity ?? false;
	const deals = checkoutFormState.get().deals ?? [];
	storefrontSettingsState.get().settings?.IsUniversity ?? false;
	const columns = useMemo(
		() =>
			orderColumns
				// remove columns we don't want
				.filter(
					(column) =>
						!isUniversity ||
						(isUniversity && !["OrderType", "Warehouse"].includes(column.id)),
				)
				// apply translations
				.map((column) => {
					// biome-ignore lint/suspicious/noExplicitAny: need any here since header can be any string
					return { ...column, header: translate(column.header as any) };
				})
				.map((column) =>
					column.id === "Deal"
						? { ...column, header: isUniversity ? "Class" : "Job" }
						: column,
				),
		[isUniversity, translate],
	);
	const filters = useMemo(
		() =>
			orderFilters
				// remove filters we don't want
				.filter(
					(filter) =>
						!isUniversity ||
						(isUniversity && !["OrderType", "Warehouse"].includes(filter.id)),
				)
				// apply translations
				.map((filter) => {
					// biome-ignore lint/suspicious/noExplicitAny: need to support any string here
					return { ...filter, label: translate(filter.label as any) };
				})
				// populate deals select
				.map((filter) =>
					filter.id === "DealId"
						? {
								...filter,
								type: "select" as const,
								label: isUniversity ? translate("Class") : translate("Job"),
								options: deals.map((deal) => ({
									label: deal.Deal,
									value: deal.DealId,
								})),
							}
						: filter,
				),
		[deals, isUniversity, translate],
	);
	const dataTableState = useDataTable<OrderRow>({
		query,
		columns,
		filters,
		quickFilters: orderQuickFilters,
		title: isUniversity ? translate("Equipment Requests") : translate("Orders"),
		description: translate(
			"Click the menu at the right of each row to get a PDF.",
		),
		emptyMessage: "No orders found",
		showQuickFilters: false,
		showPager: true,
	});

	const hasInitializedDealFilter = useRef(true); // true is disabled loading the deal filter at startup
	const onInit = useCallback(() => {
		(async () => {
			if (
				!checkoutFormState.get().deals &&
				!checkoutFormState.get().isLoadingDeals &&
				!checkoutFormState.get().isLoadingDealsFailed
			) {
				checkoutFormState.loadDealsAsync();
			}
		})();
	}, [checkoutFormState]);

	useEffect(() => {
		onInit();
	}, [onInit]);

	useEffect(() => {
		if (hasInitializedDealFilter.current) {
			return;
		}

		if (query.filters.some((filter) => filter.field === "DealId")) {
			hasInitializedDealFilter.current = true;
			return;
		}

		const selectedDealId = checkoutFormState.get().dealIdValue;
		if (!selectedDealId) {
			return;
		}

		hasInitializedDealFilter.current = true;
		void query.setFiltersAsync([
			...query.filters,
			{
				field: "DealId",
				op: "=",
				value: selectedDealId,
			},
		]);
	}, [checkoutFormState, query, query.filters]);

	return (
		<DataTable
			className="px-2 md:px-4"
			dataTableState={dataTableState}
			// childrenRightOfDescription={
			// 	<SelectDeal
			// 		hasNew={false}
			// 		hasNotSelected={true}
			// 		className="mx-2 flex gap-2 items-center"
			// 		onValueChange={async (value) => {
			// 			const filters = query.filters.filter(
			// 				(filter) => filter.field !== "DealId",
			// 			);

			// 			if (value) {
			// 				filters.push({
			// 					field: "DealId",
			// 					op: "=",
			// 					value,
			// 				});
			// 			}

			// 			await query.setFiltersAsync(filters);
			// 		}}
			// 	/>
			// }
		/>
	);
}
